import { css } from "@emotion/css";
import React, { useState } from "react";

import { Stack } from "../../../foundations/grid-layout/Stack";
import { Icon } from "../../../foundations/icons/Icon/Icon";
import { Text } from "../../../foundations/typography/Text";
import { useColors } from "../../../hooks/useColors";

export interface OuterContentItemProps {
  title?: string;
  subtitle?: string;
  children?: React.ReactNode;
  tag?: React.ReactNode;
  emptyData?: string;
  testID?: string;
}

export function OuterContentItem(props: OuterContentItemProps) {
  const {
    title,
    subtitle,
    children,
    tag,
    emptyData,
    testID = "outer-content-item",
  } = props;
  const colors = useColors();
  const [expanded, setExpanded] = useState(false);
  const onToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <div
      className={css({
        ":not(:last-child)": css({
          borderBottom: `1px solid ${colors["border-base"]}`,
        }),
      })}
      data-testid={`${testID}-outer`}
    >
      <button
        onClick={onToggle}
        tabIndex={children ? 0 : undefined}
        type="button"
        className={css([
          styles.alignCenter,
          styles.outer,
          {
            cursor: children ? "pointer" : undefined,
            "&:focus": css({
              outline: "0",
              boxShadow: "var(--focus-action)",
            }),
          },
        ])}
      >
        <div>
          <Stack gap="space-05">
            {tag ? tag : null}
            {title && (
              <Text
                as="div"
                style="summary/content-primary"
                testID={`${testID}-title`}
              >
                {title}
              </Text>
            )}
          </Stack>
          {subtitle && (
            <Text
              style="summary/content-secondary"
              color="text-weak"
              testID={`${testID}-subtitle`}
            >
              {subtitle}
            </Text>
          )}
          {emptyData && (
            <Text
              style="summary/content-secondary"
              color="text-weak"
              testID={`${testID}-empty`}
            >
              {emptyData}
            </Text>
          )}
        </div>

        {children && (
          <div className={css([styles.alignCenter])}>
            <Icon
              type="mini"
              name={expanded ? "expand_less" : "expand_more"}
              padding="space-05"
              color="icon-base"
              testID={`${testID}-expand-button`}
            />
          </div>
        )}
      </button>
      <div
        className={css(styles.outerContentItemChildren, {
          color: "var(--text-weak)",
        })}
      >
        {expanded && children}
      </div>
    </div>
  );
}

const styles = {
  outer: css({
    background: "none",
    width: "100%",
    border: "none",
    justifyContent: "space-between",
    padding: "var(--space-4)",
    textAlign: "left",
    "&:focus": css({
      outline: "0",
    }),
  }),
  alignCenter: css({
    display: "flex",
    alignItems: "center",
  }),
  outerContentItemChildren: css({
    overflow: "hidden",
    "> .subheading": {
      background: "transparent",
      marginTop: "var(--space-4)",
      marginBottom: "var(--space-4)",
    },
  }),
};
